import { useEffect, useRef, useState } from "react";
import { T } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { Btn, Card } from "../components";

const igGradient = "linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)";
const igColor    = "#e1306c";

const IgButton = ({ onClick, loading, label }) => (
  <button
    onClick={onClick}
    disabled={loading}
    style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
      width: "100%", padding: "13px 20px", borderRadius: 10, cursor: loading ? "wait" : "pointer",
      background: loading ? "rgba(225,48,108,0.6)" : igGradient, border: "none", color: "#fff",
      fontSize: 14, fontWeight: 700, transition: "opacity 0.15s",
    }}
    onMouseOver={e => { if (!loading) e.currentTarget.style.opacity = "0.9"; }}
    onMouseOut={e  => { e.currentTarget.style.opacity = "1"; }}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <circle cx="12" cy="12" r="4"/>
      <circle cx="17.5" cy="6.5" r="1.5" fill="#fff" stroke="none"/>
    </svg>
    {loading ? "Opening login…" : label}
  </button>
);

const IgSelector = ({ pages, onSelect, selecting }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
    {pages.map(p => (
      <div
        key={p.pageId}
        onClick={() => !selecting && onSelect(p.pageId)}
        style={{
          display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 12,
          cursor: selecting ? "wait" : "pointer", border: `1px solid ${T.border2}`, background: T.bg2, transition: "all 0.15s",
        }}
        onMouseOver={e => { if (!selecting) { e.currentTarget.style.borderColor = igColor; e.currentTarget.style.background = "rgba(225,48,108,0.05)"; }}}
        onMouseOut={e  => { e.currentTarget.style.borderColor = T.border2; e.currentTarget.style.background = T.bg2; }}
      >
        <div style={{ width: 40, height: 40, borderRadius: 10, background: igGradient, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
            <circle cx="12" cy="12" r="4"/>
            <circle cx="17.5" cy="6.5" r="1.5" fill="#fff" stroke="none"/>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text1 }}>@{p.igUsername || "—"}</div>
          <div style={{ fontSize: 11, color: T.text3 }}>via page: {p.pageName}</div>
        </div>
        {selecting
          ? <div style={{ width: 18, height: 18, border: `2px solid ${T.border2}`, borderTopColor: igColor, borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
          : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
        }
      </div>
    ))}
  </div>
);

export default function InstagramPage({ token }) {
  const [status,    setStatus]    = useState(null);
  const [setup,     setSetup]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [oauthStep, setOauthStep] = useState("idle");
  const [pages,     setPages]     = useState([]);
  const [selecting, setSelecting] = useState(false);
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState("");
  const [copied,    setCopied]    = useState("");

  const loadStatus = () => Promise.all([
    apiFetch("/api/instagram/status", {}, token),
    apiFetch("/api/meta/setup",       {}, token),
  ]).then(([s, su]) => { setStatus(s); setSetup(su); });

  // Load status whenever token changes
  useEffect(() => {
    if (!token) return;
    loadStatus().finally(() => setLoading(false));
    const id = setInterval(loadStatus, 30_000);
    return () => clearInterval(id);
  }, [token]);

  // Handle OAuth redirect — runs once when token becomes available
  const oauthHandled = useRef(false);
  useEffect(() => {
    if (!token || oauthHandled.current) return;
    const params    = new URLSearchParams(window.location.search);
    const metaOauth = params.get("meta_oauth");
    const platform  = params.get("platform");
    if (platform !== "instagram") return;
    oauthHandled.current = true;
    window.history.replaceState({}, "", window.location.pathname);

    if (metaOauth === "select") {
      setOauthStep("selecting");
      apiFetch("/api/meta/oauth/pages", {}, token)
        .then(d => {
          if (d.error) { setError(d.error); setOauthStep("idle"); return; }
          setPages(d.pages || []);
          if (d.pages?.length === 1) handleSelectPage(d.pages[0].pageId);
        })
        .catch(e => { setError(e.message); setOauthStep("idle"); });
    } else if (metaOauth === "error") {
      setError(params.get("msg") || "Connection failed. Make sure you selected your Instagram account during login.");
      setOauthStep("idle");
    }
  }, [token]);

  const startOAuth = async () => {
    setError(""); setOauthStep("connecting");
    try {
      const d = await apiFetch("/api/meta/oauth/start?platform=instagram", {}, token);
      if (d.error) { setError(d.error); setOauthStep("idle"); return; }
      window.location.href = d.authUrl;
    } catch (e) { setError(e.message || "Failed"); setOauthStep("idle"); }
  };

  const handleSelectPage = async (pageId) => {
    setSelecting(true); setError("");
    try {
      const d = await apiFetch("/api/meta/oauth/connect", {
        method: "POST", body: JSON.stringify({ pageId }),
      }, token);
      if (d.error) { setError(d.error); return; }
      setSuccess(`Connected to @${d.username || d.igUserId} successfully!`);
      setOauthStep("done");
      await loadStatus();
    } catch (e) { setError(e.message || "Connection failed"); }
    finally { setSelecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Instagram? Bot will stop replying to DMs.")) return;
    await apiFetch("/api/instagram/disconnect", { method: "DELETE" }, token);
    setStatus(s => ({ ...s, connected: false, username: "", igUserId: "" }));
    setSuccess("Disconnected."); setOauthStep("idle");
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(""), 2000); });
  };

  if (loading) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 32, height: 32, border: `2px solid ${T.border2}`, borderTopColor: igColor, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    </div>
  );

  const webhookUrl  = setup?.webhookUrl  || "";
  const verifyToken = setup?.verifyToken || "";

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "26px 28px 50px" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 12, background: igGradient, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
              <circle cx="12" cy="12" r="4"/>
              <circle cx="17.5" cy="6.5" r="1.5" fill="#fff" stroke="none"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.text1 }}>Instagram DMs</div>
            <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>Auto-reply to Instagram DMs using your AI knowledge base</div>
          </div>
          {status?.connected && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 20, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: "#22c55e" }}>Connected</span>
            </div>
          )}
        </div>
      </div>

      {error   && <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.3)", color: "#f43f5e", fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span>{error}</span>
        <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#f43f5e", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
      </div>}
      {success && <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.3)", color: "#22c55e", fontSize: 13, marginBottom: 16 }}>{success}</div>}

      {/* ── ACCOUNT SELECTOR ── */}
      {oauthStep === "selecting" && pages.length > 1 ? (
        <div style={{ maxWidth: 480 }}>
          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, marginBottom: 6 }}>Select your Instagram Account</div>
            <div style={{ fontSize: 12, color: T.text3, marginBottom: 20, lineHeight: 1.6 }}>Choose which account to connect:</div>
            <IgSelector pages={pages} onSelect={handleSelectPage} selecting={selecting} />
          </Card>
        </div>

      ) : status?.connected ? (
        /* ── CONNECTED ── */
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 16 }}>Account Connected</div>
            <div style={{ padding: "14px 16px", borderRadius: 12, background: T.bg0, border: `1px solid ${T.border1}`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: T.text3, marginBottom: 4 }}>Instagram Account</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text1 }}>@{status.username || "—"}</div>
              <div style={{ fontSize: 12, color: T.text3, marginTop: 2, fontFamily: "monospace" }}>ID: {status.igUserId}</div>
            </div>

            {status.unofficial ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 9, marginBottom: 14,
                background: status.lastPollOk === false ? "rgba(245,158,11,0.06)" : "rgba(34,197,94,0.06)",
                border: `1px solid ${status.lastPollOk === false ? "rgba(245,158,11,0.25)" : "rgba(34,197,94,0.25)"}` }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: status.lastPollOk === false ? "#f59e0b" : "#22c55e", flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: status.lastPollOk === false ? "#f59e0b" : "#22c55e", fontWeight: 600 }}>{status.lastPollOk === false ? "Polling needs attention" : "Polling active - checks DMs every 60 seconds"}</span>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 9, marginBottom: 14,
                background: status.webhookSubscribed ? "rgba(34,197,94,0.06)" : "rgba(245,158,11,0.06)",
                border: `1px solid ${status.webhookSubscribed ? "rgba(34,197,94,0.25)" : "rgba(245,158,11,0.25)"}` }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: status.webhookSubscribed ? "#22c55e" : "#f59e0b", flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: status.webhookSubscribed ? "#22c55e" : "#f59e0b", fontWeight: 600 }}>
                  {status.webhookSubscribed ? "Webhook subscribed — real-time DMs active" : "Webhook pending"}
                </span>
              </div>
            )}

            <Btn variant="danger" onClick={disconnect}>Disconnect Instagram</Btn>
          </Card>

          <Card>
            {status.unofficial ? (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 16 }}>Private API Mode</div>
                <div style={{ fontSize: 12, color: T.text3, lineHeight: 1.8, marginBottom: 16 }}>
                  Bot is polling your Instagram DMs every 60 seconds and replying using your AI knowledge base.<br/><br/>
                  <strong style={{ color: T.text2 }}>Keep your Instagram account logged in</strong> in the browser so the session stays active.
                </div>
                {status.lastPollOk === false ? (
                  <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)" }}>
                    <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 600, marginBottom: 2 }}>Last poll failed</div>
                    <div style={{ fontSize: 11, color: T.text3 }}>{status.lastPollError || "Reconnect Instagram and complete any login checks."}</div>
                  </div>
                ) : (
                  <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.2)" }}>
                    <div style={{ fontSize: 11, color: "#22c55e", fontWeight: 600, marginBottom: 2 }}>Session active</div>
                    <div style={{ fontSize: 11, color: T.text3 }}>{status.lastPollAt ? "Last poll completed successfully." : "Waiting for the first poll after connection."}</div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 16 }}>Webhook Info</div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: T.text2, marginBottom: 5 }}>Callback URL</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input readOnly value={webhookUrl} style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg0, color: T.text2, fontSize: 11, fontFamily: "monospace", outline: "none" }} />
                    <Btn variant="secondary" onClick={() => copy(webhookUrl, "url")}>{copied === "url" ? "Copied!" : "Copy"}</Btn>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: T.text2, marginBottom: 5 }}>Verify Token</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <input readOnly value={verifyToken} style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg0, color: T.text2, fontSize: 11, fontFamily: "monospace", outline: "none" }} />
                    <Btn variant="secondary" onClick={() => copy(verifyToken, "vt")}>{copied === "vt" ? "Copied!" : "Copy"}</Btn>
                  </div>
                </div>
              </>
            )}
          </Card>
        </div>

      ) : (
        /* ── CONNECT ── */
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

          {/* Left — Quick Connect via private login */}
          <Card>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e" }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text1 }}>Quick Connect - Private Login</div>
            </div>
            <div style={{ fontSize: 12, color: T.text3, marginBottom: 14, lineHeight: 1.6 }}>
              Works instantly — no App Review needed, works with any Instagram account.
            </div>

            <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)", marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#3b82f6", marginBottom: 4 }}>How to connect</div>
              <div style={{ fontSize: 11, color: T.text3, lineHeight: 1.9 }}>
                1. Use an Instagram account that is already trusted and not checkpointed<br/>
                2. Enter username and password below<br/>
                3. If 2FA is enabled, enter the code and connect again
              </div>
            </div>

            <SessionConnectForm token={token} onConnected={async (username) => { await loadStatus(); setSuccess(`Connected as @${username}!`); }} />

            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "18px 0 14px" }}>
              <div style={{ flex: 1, height: 1, background: T.border1 }} />
              <span style={{ fontSize: 11, color: T.text3 }}>or use official OAuth</span>
              <div style={{ flex: 1, height: 1, background: T.border1 }} />
            </div>

            <IgButton onClick={startOAuth} loading={oauthStep === "connecting"} label="Connect with Instagram (Official)" />
          </Card>

          {/* Right — info */}
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 16 }}>How it works</div>
            <div style={{ fontSize: 12, color: T.text3, lineHeight: 1.9, marginBottom: 18 }}>
              The bot polls your Instagram DMs every 60 seconds and replies using your AI knowledge base.
              Works with any account type — personal, business, or creator.
            </div>

            <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.2)", marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 600, marginBottom: 3 }}>Important</div>
              <div style={{ fontSize: 11, color: T.text3, lineHeight: 1.7 }}>
                Keep your account logged in on Instagram so the session stays active.
                If the session expires, reconnect and complete any Instagram login check.
              </div>
            </div>

            <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(244,63,94,0.06)", border: "1px solid rgba(244,63,94,0.2)" }}>
              <div style={{ fontSize: 11, color: "#f43f5e", fontWeight: 600, marginBottom: 3 }}>Disclaimer</div>
              <div style={{ fontSize: 11, color: T.text3, lineHeight: 1.7 }}>
                Private login uses Instagram's internal API. Use responsibly - avoid high message volume to reduce ban risk.
              </div>
            </div>

            <div style={{ marginTop: 14 }}>
              <button onClick={() => setOauthStep("idle")} style={{ background: "none", border: "none", color: T.text3, fontSize: 11, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                Paste Page Access Token manually instead
              </button>
            </div>
            {oauthStep === "idle" && <ManualConnectForm token={token} onConnected={async () => { await loadStatus(); setSuccess("Connected!"); }} />}
          </Card>
        </div>
      )}
    </div>
  );
}

// ── Private login connect ────────────────────────────────────────────────
function SessionConnectForm({ token, onConnected }) {
  const [username,  setUsername]  = useState("");
  const [password,  setPassword]  = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [show,      setShow]      = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");

  const connect = async () => {
    setError("");
    if (!username.trim()) return setError("Instagram username is required");
    if (!password.trim()) return setError("Instagram password is required");
    setSaving(true);
    try {
      const d = await apiFetch("/api/instagram/unofficial/connect", {
        method: "POST", body: JSON.stringify({ username: username.trim(), password, verificationCode: verificationCode.trim() }),
      }, token);
      if (d.error) { setError(d.error); return; }
      setUsername(""); setPassword(""); setVerificationCode("");
      onConnected(d.username);
    } catch (e) { setError(e.message || "Connection failed"); }
    finally { setSaving(false); }
  };

  const inp = (label, value, setter, placeholder, secret) => (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: T.text2, display: "block", marginBottom: 4 }}>{label}</label>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          type={secret && !show ? "password" : "text"}
          value={value} onChange={e => setter(e.target.value)} placeholder={placeholder}
          style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg0, color: T.text1, fontSize: 12, fontFamily: "monospace", outline: "none" }}
        />
        {secret && <button onClick={() => setShow(v => !v)} style={{ padding: "0 10px", borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg0, color: T.text3, cursor: "pointer", fontSize: 11 }}>{show ? "Hide" : "Show"}</button>}
      </div>
    </div>
  );

  return (
    <div>
      {error && <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.3)", color: "#f43f5e", fontSize: 12, marginBottom: 10 }}>{error}</div>}
      {inp("Instagram username  (without @)", username, setUsername, "zepliqai", false)}
      {inp("Password", password, setPassword, "Instagram password", true)}
      {inp("2FA code  (only if required)", verificationCode, setVerificationCode, "6-digit code", false)}
      <Btn variant="primary" onClick={connect} disabled={saving}>{saving ? "Connecting…" : "Connect"}</Btn>
    </div>
  );
}

// ── Manual token fallback ──────────────────────────────────────────────────
function ManualConnectForm({ token, onConnected }) {
  const [open,            setOpen]            = useState(false);
  const [pageAccessToken, setPageAccessToken] = useState("");
  const [showToken,       setShowToken]       = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState("");

  const connect = async () => {
    setError("");
    if (!pageAccessToken.trim()) return setError("Page Access Token is required");
    setSaving(true);
    try {
      const d = await apiFetch("/api/instagram/connect", {
        method: "POST", body: JSON.stringify({ pageAccessToken: pageAccessToken.trim() }),
      }, token);
      if (d.error) { setError(d.error); return; }
      setPageAccessToken(""); onConnected();
    } catch (e) { setError(e.message || "Connection failed"); }
    finally { setSaving(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} style={{ background: "none", border: "none", color: T.text3, fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
      Paste token manually instead
    </button>
  );

  return (
    <div style={{ marginTop: 4 }}>
      {error && <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.3)", color: "#f43f5e", fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <label style={{ fontSize: 11, fontWeight: 600, color: T.text2, display: "block", marginBottom: 6 }}>Page Access Token</label>
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <input type={showToken ? "text" : "password"} value={pageAccessToken} onChange={e => setPageAccessToken(e.target.value)} placeholder="EAAxxxxxxxxxxxxxx..."
          style={{ flex: 1, padding: "9px 12px", borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg0, color: T.text1, fontSize: 13, outline: "none" }} />
        <button onClick={() => setShowToken(v => !v)} style={{ padding: "0 12px", borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg0, color: T.text3, cursor: "pointer", fontSize: 12 }}>
          {showToken ? "Hide" : "Show"}
        </button>
      </div>
      <Btn variant="primary" onClick={connect} disabled={saving}>{saving ? "Connecting..." : "Connect"}</Btn>
    </div>
  );
}
